import React from 'react';
import BotMessageEditor from '@common-components/bt-chatbot/bot-components/bot-message-editor';
import RowStack from '@common-components/common/row-stack/RowStack';
import { WhatsappIdCell } from '@common-components/tasks/columns-cells/whatsapp-id-cell/whatsapp-id-cell';
import { SearchBox } from '@common-components/tasks/person-picker/search-box';
import { getNodeDataByNodeId } from '@common-reducers/ChatbotSelectors';
import { changeNodeDataThunk } from '@common-reducers/ChatbotThunks';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { UnifiedContactSelector } from '@extension/context/content/components/shared';
import { waQueryContactsAndChatsTool } from '@common/tools/whatsapp';
import { Cancel } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton } from '@mui/material';
import { Stack } from '@mui/system';
import { useEffect, useState } from 'react';
import LLMNodeFooter from '@common-components/bt-chatbot/bt-nodes/LLM/llm-node-footer';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import type { Asset } from '@common/types/interface';

interface MessageNodeData {
    type: string;
    contactId?: string;
    text?: string;
    asset?: any;
}

interface Contact {
    id: {
        _serialized: string;
    };
}

interface Props {
    id: string;
}

function SendMessageNodeContainer({ id }: Props) {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();

    const [searchTerm, setSearchTerm] = useState('');
    const [isContactPickerOpen, setIsContactPickerOpen] = useState(false);

    const data = useAppSelector(state => getNodeDataByNodeId(state, id)) as MessageNodeData;

    const onChange = (value: { message: string; asset?: Asset }) => {
        void dispatch(changeNodeDataThunk({
            id,
            dataToPatch: { text: value.message, asset: value.asset }
        }));
    };

    const setNewContactValue = async (contact: Contact | null) => {
        setIsContactPickerOpen(false);
        await dispatch(changeNodeDataThunk({
            id,
            dataToPatch: { contactId: contact?.id?._serialized || '' }
        }));
        setSearchTerm('');
    };

    useEffect(() => {
        try {
            waQueryContactsAndChatsTool({})
        } catch (e) {
            console.error("[waQueryContactsAndChatsTool] error", e)
        }
    }, [dispatch]);

    return (
        <>
            {data?.type === 'messageContact' && (
                <Stack sx={{
                    width: '100%',
                    marginBottom: '10px',
                    justifyContent: 'center',
                }}>
                    <RowStack sx={{ height: '40px', }}>
                        <WhatsappIdCell
                            cellValue={data?.contactId}
                            taskId={id.substring(0, 24)}
                            columnId={id.substring(0, 24)}
                            openPicker={() => setIsContactPickerOpen(true)}
                            onUpdate={() => { }}
                            togglePicker={() => { }}
                            closePicker={() => { }}
                        />
                        {data?.contactId && data?.contactId !== '' && (
                            <IconButton onClick={() => setNewContactValue(null)}>
                                <Cancel />
                            </IconButton>
                        )}
                    </RowStack>
                    {isContactPickerOpen && (
                        <>
                            <Stack direction="row" justifyContent={'center'}>
                                <SearchBox
                                    variant="outlined"
                                    autoComplete='off'
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder={t('chatbot.enterNameOrNumber')}
                                    InputProps={{
                                        endAdornment: <SearchIcon sx={{ fontSize: 16, color: 'var(--color-text)' }} />,
                                    }}
                                    fullWidth
                                />
                            </Stack>
                            {searchTerm !== '' && (
                                <div
                                    className="nowheel"
                                    style={{
                                        zIndex: 1000,
                                        backgroundColor: 'var(--color-surface)',
                                    }}
                                >
                                    <UnifiedContactSelector
                                        showSearch={false}
                                        showRefreshButton
                                        height={175}
                                        externalSearchValue={searchTerm}
                                        onContactClick={setNewContactValue}
                                        allowFreeInput
                                        onFreeInputAdd={({ chatId }) => setNewContactValue({ id: { _serialized: chatId } })}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </Stack>
            )}
            <BotMessageEditor
                type={data?.type}
                _message={{ message: data?.text ?? '', asset: data?.asset }}
                onChange={onChange}
            />
            {data?.type === 'LLM' && 
                <LLMNodeFooter 
                    id={id}
                />
            }
        </>
    );
}

export default React.memo(SendMessageNodeContainer);
